import { BASE_URL } from "../../data/constants";
import { getToken } from "../authStore";
import { http } from "../http";

/**
 * Firmware-releaser för fjärruppdatering.
 *
 * Livscykeln är fyra steg och de är inte utbytbara mot varandra:
 *
 *   draft    uppladdad, serveras inte till någon
 *   canary   bara sensorer som pinnats till den
 *   stable   alla sensorer med OTA påslaget
 *   archived tas inte ut längre, raden finns kvar
 *
 * En uppladdning hamnar alltid i draft. Att lägga upp en binär och att
 * rulla ut den ska kräva två separata beslut.
 */

export type FirmwareStatus = "draft" | "canary" | "stable" | "archived";

export type FirmwareRelease = {
  id: string;
  version: string;
  notes: string | null;
  sizeBytes: number;
  sha256: string;
  status: FirmwareStatus;
  createdAt: string;
  /** Antal sensorer som pekats om till just den här releasen. */
  pinnedSensors: number;
};

export type FirmwareListResponse = {
  maxBytes: number;
  appPartitionBytes: number;
  maxOtaAttempts: number;
  items: FirmwareRelease[];
};

export function listFirmware() {
  return http.get<FirmwareListResponse>("/admin/firmware");
}

/**
 * Går inte via http.ts: den sätter Content-Type till application/json,
 * och en multipart-uppladdning måste låta webbläsaren sätta headern
 * själv för att boundaryn ska komma med.
 */
export async function uploadFirmware(params: {
  version: string;
  notes?: string;
  file: File;
}) {
  const form = new FormData();
  form.append("version", params.version);
  if (params.notes) form.append("notes", params.notes);
  form.append("firmware", params.file);

  const token = getToken();
  const res = await fetch(`${BASE_URL}/admin/firmware`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  const body = await res.json().catch(() => null);

  if (!res.ok) {
    // Servern svarar med maskinläsbara felkoder. Att skicka vidare hint
    // när den finns sparar en resa till serverloggen.
    const detail = body?.hint ?? body?.error ?? res.statusText;
    throw new Error(String(detail));
  }

  return body as { ok: true; release: FirmwareRelease };
}

export function setFirmwareStatus(id: string, status: FirmwareStatus) {
  return http.patch<{ ok: true; release: FirmwareRelease }>(
    `/admin/firmware/${encodeURIComponent(id)}`,
    { status }
  );
}

export function deleteFirmware(id: string) {
  return http.delete<{ ok: true }>(`/admin/firmware/${encodeURIComponent(id)}`);
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
